/* global grecaptcha */

import loadExternalScript from 'Models/load-external-script';
import { dispatch, listen, unlisten } from 'Models/utils/event-bus';
import ValidForm from '@pageclip/valid-form';
import fetchData from 'Models/fetch-data';
import * as ProjectConfig from 'Models/utils/project-config';
import trackEvent from 'Models/tracking';
import reportValidity from 'report-validity';

export default function form (element) {
    const config = JSON.parse(element.dataset.formConfig);
    const formId = element.dataset.formId;
    const trackingName = element.dataset.trackingName;

    const settings = {
        recaptchaSiteKey: ProjectConfig.get('recaptchaSiteKey'),
        recaptchaScriptUrl: 'https://www.google.com/recaptcha/api.js?render=explicit',
        recaptchaLoadCallbackHandler: 'captchaLoaded',
    };

    const elements = {
        captcha: element.querySelector('.js-form__captcha'),
        success: document.querySelector('.js-form__success'),
        captchaError: element.querySelector('.js-form__captcha-error'),
        uploadFields: element.querySelectorAll('.js-upload'),
        accordionBodies: element.querySelectorAll('.js-accordion__body'),
        radioButtons: element.querySelectorAll('input[type="radio"]'),
    };

    const states = {
        isVisible: 'is-visible',
        isSuccess: 'is-success',
        isProgress: 'is-progress',
        isOpen: 'is-open',
    };

    let captcha;
    let captchaListenersPresent = false;
    let captchaError = false;
    let backendError = false;
    let progress = false;

    let fileData = false;

    function init () {
        initValidation();
        addListeners();
        if (config.captcha) {
            if (config.initCaptchaOnEvent) {
                addCaptchaListeners();
            } else {
                maybeLoadCaptcha();
            }
        }
        if (config.submitFormOnEvent) {
            addExternalFormSubmitListeners();
        }
    }

    function addListeners () {
        element.addEventListener('submit', function (e) {
            e.preventDefault();
            submitForm();
            return false;
        });

        elements.radioButtons.forEach((btn) => {
            btn.addEventListener('click', handleRadioButtonClick);
        });

        // listen to file uploads and append to formdata
        listen(`upload-file-${formId}`, payload => {
            if (!fileData) {
                fileData = [payload];
            } else {
                const alreadyExistingFile = fileData.find(item => { return item.key === payload.key; });
                if (alreadyExistingFile) {
                    alreadyExistingFile.value = payload.value;
                } else {
                    fileData.push(payload);
                }
            }
        });

        elements.accordionBodies.forEach(accordionBody => {
            const formFields = accordionBody.querySelectorAll('.form-builder__field');
            const lastFormField = formFields[formFields.length - 1];
            const formItems = lastFormField.querySelectorAll('.form__item');
            const formFieldInputType = formItems[0].classList[1];
            const formItem = formItems[0];

            switch (formFieldInputType) {
            case 'form__item--textarea': {
                const textArea = formItem.querySelector('textarea');
                textArea.addEventListener('input', handleInput);
                break;
            }
            case 'form__item--select': {
                const select = formItem.querySelector('select');
                select.addEventListener('change', handleInput);
                break;
            }

            case 'form__item--text':
            case 'form__item--email':
            case 'form__item--tel': {
                const textInput = formItem.querySelector('input');
                textInput.addEventListener('input', handleInput);
                break;
            }

            case 'form__item--rating':
            case 'form__item--radio':
            case 'form__item--checkbox': {
                formItems.forEach((rating) => {
                    const ratingInput = rating.querySelector('input');
                    ratingInput.addEventListener('change', handleInput);
                });
                break;
            }
            }
        });
    }

    function handleRadioButtonClick (e) {
        if (e.target.classList.contains('is-checked')) {
            e.target.classList.remove('is-checked');
            e.target.checked = false;
        } else {
            elements.radioButtons.forEach((btn) => {
                btn.classList.remove('is-checked');
            });
            e.target.classList.add('is-checked');
        }
    }

    function handleInput (e) {
        const accordionItem = e.target.closest('.accordion__item');
        const nextElementSibling = accordionItem.nextElementSibling;
        const isNextAccordion = nextElementSibling.classList.contains('accordion__item');

        if (!isNextAccordion) {
            return;
        }

        const nextAccordionHead = nextElementSibling.querySelector('.js-accordion__head');

        // if (!nextAccordionHead.classList.contains(states.isOpen)) {
        //    nextAccordionHead.click();
        // }
    }

    function addCaptchaListeners () {
        listen(config.initCaptchaOnEvent, maybeLoadCaptcha);
        captchaListenersPresent = true;
    }

    function removeCaptchaListeners () {
        unlisten(config.initCaptchaOnEvent, maybeLoadCaptcha);
        captchaListenersPresent = false;
    }

    function addExternalFormSubmitListeners () {
        listen(config.submitFormOnEvent, submitFormFromExternalEvent);
    }

    function maybeLoadCaptcha () {
        if (captchaListenersPresent) {
            removeCaptchaListeners();
        }
        typeof grecaptcha === 'undefined' ? loadCaptcha() : initCaptcha();
    }

    async function loadCaptcha () {
        await loadExternalScript(settings.recaptchaScriptUrl + '&onload=' + settings.recaptchaLoadCallbackHandler);
        await internalCaptchaInitialization();
        initCaptcha();
    }

    function internalCaptchaInitialization () {
        return new Promise(resolve => {
            window[settings.recaptchaLoadCallbackHandler] = function () {
                resolve('resolved');
            };
        });
    }

    function initCaptcha () {
        captcha = grecaptcha.render(
            elements.captcha,
            {
                sitekey: settings.recaptchaSiteKey,
                callback: captchaChangedCallback,
            },
        );
    }

    function initValidation () {
        ValidForm(element, {
            invalidClass: 'form__field--invalid',
            validationErrorClass: 'form__error',
            errorPlacement: 'after',
            customMessages: ProjectConfig.get('generalErrorMessages'),
        });
    }

    function validateCaptcha () {
        const response = grecaptcha.getResponse(captcha.get);
        return !!(response && response !== '');
    }

    function validateForm () {
        let error = false;
        if (!reportValidity(element)) {
            error = true;
        }

        if (config.captcha && !validateCaptcha()) {
            showCaptchaError();
            error = true;
        } else if (config.captcha && captchaError) {
            hideCaptchaError();
        }

        return !error;
    }

    function showCaptchaError () {
        captchaError = true;
        elements.captchaError.classList.add(states.isVisible);
    }

    function hideCaptchaError () {
        captchaError = false;
        elements.captchaError.classList.remove(states.isVisible);
    }

    function captchaChangedCallback (data) {
        if (data && captchaError) {
            hideCaptchaError();
        }
    }

    function submitFormFromExternalEvent (data = false) {
        const additionalData = data && data.additionalData ? data.additionalData : false;
        submitForm(additionalData);
    }

    function submitForm (additionalData = false) {
        if (progress) {
            return false;
        }
        if (!validateForm()) {
            return false;
        }

        const formData = new FormData(element);

        if (additionalData) {
            additionalData.forEach(dataItem => {
                formData.append(dataItem.key, dataItem.value);
            });
        }

        if (fileData) {
            fileData.forEach(dataItem => {
                formData.append(dataItem.key, dataItem.value);
            });
        }

        sendForm(formData);
    }

    async function sendForm (formData) {
        // for (var [key, value] of formData.entries()) {
        //     console.log(key, value);
        // }
        showProgress();
        try {
            const response = await fetchData(config.action, 'POST', formData, false);
            formSent(response);
            hideProgress();
        } catch (e) {
            hideProgress();
            console.error(e);
        }
    }

    function formSent (response) {
        if (response.status === 'success') {
            showSuccess();
        } else if (response.status === 'error') {
            showError(response.data.message);
        }
    }

    function showProgress () {
        progress = true;
        if (config.triggerEvents) {
            dispatch(config.triggerEvents, { type: 'progress', show: true });
        }
        element.classList.add(states.isProgress);
    }

    function hideProgress () {
        progress = false;
        if (config.triggerEvents) {
            dispatch(config.triggerEvents, { type: 'progress', show: false });
        }
        element.classList.remove(states.isProgress);
    }

    function showSuccess () {
        if (config.triggerEvents) {
            dispatch(config.triggerEvents, { type: 'success' });
        }
        element.reset();
        if (backendError) {
            hideError();
        }

        element.parentNode.classList.add(states.isSuccess);
        const successMessage = element.querySelector('.js-form__success');
        if (successMessage) {
            successMessage.scrollIntoView({
                block: 'center',
            });
        }

        trackEvent('Form', trackingName, trackingName, window.location.href, true);
    }

    function showError (message) {
        const errorEl = document.createElement('div');
        errorEl.classList.add('form__error');
        errorEl.classList.add('js-form__form-error');
        errorEl.innerText = message;
        element.appendChild(errorEl);
        backendError = true;
    }

    function hideError () {
        const errorEl = element.querySelector('.js-form__form-error');
        errorEl.parentNode.removeChild(errorEl);
        backendError = false;
    }

    init();
}
